
import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from "react";
import { supabase, readCachedSession, tokenCircuitTripped, clearStoredAuthToken, revokeLoginOnServer } from "../supabaseClient";
import { durableGet, durableSet, durableRemove } from "../lib/durableStore";
import { persistOfflineIdentity, readOfflineIdentity, markSignedOut, clearSignedOut, isSignedOut, isSignedOutSync } from "../lib/offlineIdentity";
import { drainSubmitQueue } from "../lib/submitQueue";
import { TOKENLESS_EVENT } from "../lib/tokenlessCall";

const AuthContext = createContext({});

export function useAuth() {
    return useContext(AuthContext);
}

// Like readCachedSession, but falls back to the IndexedDB durable mirror when
// localStorage lost the copy (and heals localStorage from it).
async function readCachedSessionDurable() {
    const fromLocal = readCachedSession();
    if (fromLocal) return fromLocal;
    const raw = await durableGet("seapa_session_backup");
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (parsed?.user) {
            try { localStorage.setItem("seapa_session_backup", raw); } catch { /* quota */ }
            return parsed;
        }
    } catch { /* corrupt */ }
    return null;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [roles, setRoles] = useState([]);
    // The landing-page decision must WAIT for this: an empty roles list can
    // mean "still fetching" (fresh install, no cache), and picking a home
    // page off an empty list dumps people on the workspace hub.
    const [rolesLoaded, setRolesLoaded] = useState(false);
    const [boardPosition, setBoardPosition] = useState(null);
    // Training committee positions (trainee_organizer, deputy_organizer,
    // auditor, training_committee) — multi-valued, separate from the single
    // board_position. Loaded non-blocking, same as boardPosition.
    const [committeePositions, setCommitteePositions] = useState([]);
    // position-gated routes must WAIT for this instead of denying while the
    // async position lookup is still in flight (deep links bounced otherwise)
    const [positionLoaded, setPositionLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [longLoad, setLongLoad] = useState(false); // New: detect hangs
    const [offline, setOffline] = useState(false); // booted from saved session / lost network
    // Back online but the server no longer honors any saved login: the app
    // KEEPS RUNNING on the device identity (never kick someone out of the
    // slip they're working on); a banner asks them to sign in to sync.
    const [needsReauth, setNeedsReauth] = useState(false);
    // UI-readable twin of explicitSignOutRef: the login page auto-resumes
    // device work UNLESS the user really tapped Sign Out.
    // Starts from the persisted flag so a sign-out survives closing the app
    // (2026-09-24): before, a relaunch forgot it and auto-resumed.
    const [signedOutExplicitly, setSignedOutExplicitly] = useState(isSignedOutSync);
    const loadingRef = useRef(true); // timers need the CURRENT value, not a stale closure
    const sessionRef = useRef(null); // auth event handlers need the CURRENT session
    const mountedRef = useRef(true);
    const lastUserIdRef = useRef(null); // Track user ID to prevent redundant fetches
    const explicitSignOutRef = useRef(isSignedOutSync()); // only a user-tapped Sign Out may log us out while offline
    // True while Sign Out's background client reset runs: a token refresh
    // that was already in flight must not restore the login it just ended.
    const signingOutRef = useRef(false);
    const signOutSupersededRef = useRef(false); // a new sign-in landed during that reset
    // The quick copy of the signed-out flag lives in localStorage; if iOS
    // dropped that but kept the IndexedDB mirror, pick it up here (Codex
    // review of #506). The login page also checks it before auto-resuming.
    const realLoginSeenRef = useRef(false); // a session-carrying auth event landed
    useEffect(() => {
        isSignedOut({ restore: true }).then((v) => {
            if (!v || !mountedRef.current) return;
            // A sign-in that beat this slow IndexedDB read wins: undo the
            // quick copy isSignedOut() just restored (Codex review of #506).
            if (realLoginSeenRef.current) { clearSignedOut(); return; }
            explicitSignOutRef.current = true; setSignedOutExplicitly(true);
        });
    }, []);

    const fetchingRolesRef = useRef(false);
    // Throttle probe-driven token refreshes. A travel day flaps online/offline
    // many times, and each 'online'/focus event fires the probe; without this,
    // every flip triggered a refresh, drifting the backup token behind the
    // rotated live one and priming the refresh storm (2026-07-18). At most one
    // probe refresh attempt per PROBE_REFRESH_MIN_MS.
    const lastProbeRefreshRef = useRef(0);

    // Helper with timeout (Reduced to 10s)
    const withTimeout = (promise, ms = 10000) => {
        return Promise.race([
            promise,
            new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), ms)),
        ]);
    };

    async function loadRoles(userId, onCacheHit = null) {
        if (!userId) return [];
        const CACHE_KEY = `roles_${userId}`;
        fetchingRolesRef.current = true; // Lock

        // 1. Check cache immediately
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached && onCacheHit) {
            console.log("AuthContext: Cache hit, using stored roles immediately.");
            onCacheHit(JSON.parse(cached));
        }

        try {
            console.log("AuthContext: fetching roles for", userId);

            // USE RPC to avoid RLS Recursion (Hang)
            const { data, error } = await withTimeout(
                supabase.rpc('get_user_roles', { p_user_id: userId })
                , 5000); // 5s timeout for roles

            if (error) {
                // Fallback to table select if RPC fails (e.g. not applied yet)
                console.warn("AuthContext: RPC get_user_roles failed, falling back to direct select...", error);

                const { data: fallbackData, error: fallbackError } = await withTimeout(
                    supabase
                        .from("user_roles")
                        .select("role")
                        .eq("auth_user_id", userId)
                        .eq("active", true)
                    , 5000);

                if (fallbackError) {
                    // Try legacy auth_id
                    if (fallbackError.code === '42703') {
                        const { data: legacyData, error: legacyError } = await withTimeout(
                            supabase.from("user_roles").select("role").eq("auth_id", userId).eq("active", true)
                            , 5000);
                        if (legacyError) throw legacyError;
                        const rolesList = (legacyData || []).map(r => r.role);
                        localStorage.setItem(CACHE_KEY, JSON.stringify(rolesList));
                        return rolesList;
                    }
                    throw fallbackError;
                }
                const rolesList = (fallbackData || []).map(r => r.role);
                localStorage.setItem(CACHE_KEY, JSON.stringify(rolesList));
                return rolesList;
            }

            // RPC returns array of objects { role: '...' } directly because of RETURNS TABLE (role text)
            // Wait, RPC returns [{ role: 'pilot' }]? Yes.
            const rolesList = (data || []).map((r) => r.role);

            // Cache success
            localStorage.setItem(CACHE_KEY, JSON.stringify(rolesList));
            return rolesList;

        } catch (err) {
            // If we have cache, this is a minor warning (background refresh failed).
            // If no cache, it's a real error (user blocked).
            if (cached) {
                console.warn("AuthContext: Background role refresh timed out. Keeping cached roles.");
                return JSON.parse(cached);
            }

            console.error("AuthContext: loadRoles crash/timeout", err);
            return [];
        } finally {
            fetchingRolesRef.current = false; // Unlock
        }
    }

    // Load the logged-in user's board seat (president, vice_president, ...) for
    // position-gated access. Non-blocking — never holds up login. Committee
    // positions (training_chair, auditor, ...) come from get_my_positions
    // below; the shared position mailboxes grant nothing (Jill 2026-10-07).
    async function loadBoardPosition(authUserId, email) {
        if (!authUserId) return null;
        const CACHE_KEY = `boardpos_${authUserId}`;
        try {
            let { data } = await withTimeout(
                supabase.from("pilots_info").select("board_position").eq("auth_user_id", authUserId).maybeSingle()
                , 5000);
            if ((!data || data.board_position == null) && email) {
                const r = await withTimeout(
                    supabase.from("pilots_info").select("board_position").ilike("email", email).maybeSingle()
                    , 5000);
                data = r.data || data;
            }
            const pos = data?.board_position ?? null;
            localStorage.setItem(CACHE_KEY, JSON.stringify(pos));
            return pos;
        } catch (err) {
            const cached = localStorage.getItem(CACHE_KEY);
            return cached ? JSON.parse(cached) : null;
        }
    }

    // Committee positions via the get_my_positions RPC (matches the linked
    // auth_user_id only; a roster row with no login holds no position).
    async function loadCommitteePositions(authUserId) {
        if (!authUserId) return [];
        const CACHE_KEY = `committeepos_${authUserId}`;
        try {
            const { data, error } = await withTimeout(supabase.rpc("get_my_positions"), 5000);
            if (error) throw error;
            const list = (data || []).map(r => (typeof r === "string" ? r : r.position ?? r.get_my_positions)).filter(Boolean);
            localStorage.setItem(CACHE_KEY, JSON.stringify(list));
            return list;
        } catch {
            const cached = localStorage.getItem(CACHE_KEY);
            return cached ? JSON.parse(cached) : [];
        }
    }

    useEffect(() => { loadingRef.current = loading; }, [loading]);
    useEffect(() => { sessionRef.current = session; }, [session]);

    // Open the app from whatever this device still holds, newest layer first:
    // saved session tokens (localStorage, then IndexedDB mirror), else the
    // token-less offline identity. Tokens are only needed to SYNC — showing a
    // pilot their own device's slip and voyage plan must never require one
    // (Jill's iPad 2026-07-11: iOS discarded the login tokens; drafts and
    // last-screen were intact but the app dead-ended at an unusable login page).
    // Returns true if it booted; requireStillLoading guards the boot-timer path
    // so it can't stomp a login that settled normally. opts.markOffline
    // (default true) sets the offline banner; the local-first boot passes false
    // so opening from the saved session doesn't declare us offline on a hunch.
    // A data call left this device WITHOUT a login token: supabase-js had no
    // stored session and fell back to the public key, so the server answers
    // as anonymous and the call can only fail. If someone is on screen, their
    // sign-in is gone underneath them (announced by an auth event, then
    // dropped by the browser). Raise the sign-in banner instead of letting
    // every page fail quietly (John Herring's iPhone Chrome, 2026-10-30: kept
    // nothing after a reset link; "Failed to load profile", feedback 401).
    // The banner is hidden while offline, and the next SIGNED_IN or
    // TOKEN_REFRESHED clears it.
    // A notice that lands before anyone is on screen (the layout's first
    // calls race the device boot) is remembered, and acted on the moment a
    // device boot puts a user on screen without a client session (Codex on
    // #632: a page that only loads once would otherwise never show it).
    const tokenlessPendingRef = useRef(false);
    useEffect(() => {
        const onTokenless = (e) => {
            if (!mountedRef.current || explicitSignOutRef.current) return;
            if (!sessionRef.current?.user) { tokenlessPendingRef.current = true; return; }
            e.preventDefault(); // acted on: starts the notifier's quiet window
            tokenlessPendingRef.current = false;
            console.warn("AuthContext: a data call left without a login token — asking for re-sign-in", e?.detail?.url);
            setNeedsReauth(true);
        };
        window.addEventListener(TOKENLESS_EVENT, onTokenless);
        return () => window.removeEventListener(TOKENLESS_EVENT, onTokenless);
    }, []);

    const bootFromDevice = useCallback(async (requireStillLoading, opts = {}) => {
        if (!mountedRef.current) return false;
        if (requireStillLoading && !loadingRef.current) return false;
        // Signed out on purpose: only the login page's Continue offline tap
        // (opts.explicit) opens the device's saved work, never an automatic
        // path (Jill 2026-09-24).
        if (!opts.explicit && await isSignedOut()) return false;
        const cached = await readCachedSessionDurable();
        const identity = cached?.user ? null : await readOfflineIdentity();
        const sessionLike = cached?.user
            ? cached
            : identity?.userId
                ? { user: { id: identity.userId, email: identity.email || "" }, offline_identity: true }
                : null;
        if (!sessionLike) return false;
        // re-check after the awaits — a real auth event may have landed meanwhile
        if (!mountedRef.current) return false;
        if (requireStillLoading && !loadingRef.current) return false;
        console.warn("AuthContext: booting from device storage", cached?.user ? "(saved session)" : "(offline identity, no tokens)");
        lastUserIdRef.current = sessionLike.user.id;
        setSession(sessionLike);
        setUser(sessionLike.user);
        if (tokenlessPendingRef.current) {
            tokenlessPendingRef.current = false;
            console.warn("AuthContext: data calls already left without a login token — asking for re-sign-in");
            setNeedsReauth(true);
        }
        try {
            const cachedRoles = localStorage.getItem(`roles_${sessionLike.user.id}`);
            if (cachedRoles) setRoles(JSON.parse(cachedRoles));
            else if (identity?.roles) {
                setRoles(identity.roles);
                try { localStorage.setItem(`roles_${sessionLike.user.id}`, JSON.stringify(identity.roles)); } catch { /* quota */ }
            }
            const cachedPos = localStorage.getItem(`boardpos_${sessionLike.user.id}`);
            if (cachedPos) setBoardPosition(JSON.parse(cachedPos));
            else if (identity?.boardPosition != null) setBoardPosition(identity.boardPosition);
            const cachedCommittee = localStorage.getItem(`committeepos_${sessionLike.user.id}`);
            if (cachedCommittee) setCommitteePositions(JSON.parse(cachedCommittee));
        } catch { /* render with whatever we recovered */ }
        setPositionLoaded(true);
        setRolesLoaded(true);
        if (opts.markOffline !== false) setOffline(true);
        setLoading(false);
        return true;
    }, []);

    // Reconnect probe (John, 2026-07-08: LTE fine but the offline banner sat
    // for minutes). Offline mode was sticky — it only cleared on a token
    // refresh, which can be an hour away. While the banner is up, actively
    // ping a tiny auth endpoint every 25s (plus immediately on app focus and
    // on the browser's own 'online' event); the moment one lands, clear the
    // banner and force a session refresh so writes work again right away.
    useEffect(() => {
        if (!offline) return;
        let stopped = false;
        const probe = async () => {
            try {
                const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/auth/v1/health`, {
                    method: "GET",
                    cache: "no-store",
                    headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
                    ...(typeof AbortSignal !== "undefined" && AbortSignal.timeout ? { signal: AbortSignal.timeout(4000) } : {}),
                });
                if (!stopped && res.ok && mountedRef.current) {
                    setOffline(false);
                    drainSubmitQueue(); // network is really back — send queued submissions
                    // The refresh circuit breaker is open (too many recent
                    // refreshes): do NOT pile on. The saved tokens are almost
                    // certainly stale/rotated — re-feeding them is exactly what
                    // fed the storm (2026-07-18). Ask for a fresh sign-in and
                    // let the app keep running on the device identity.
                    if (tokenCircuitTripped()) {
                        console.warn("AuthContext: refresh circuit open — skipping probe refresh, asking for re-sign-in");
                        if (mountedRef.current) setNeedsReauth(true);
                        return;
                    }
                    // Throttle: at most one probe-driven refresh per window, so
                    // a burst of online/focus/visibility events can't fan out
                    // into a burst of refreshes.
                    const PROBE_REFRESH_MIN_MS = 15000;
                    const sinceLast = Date.now() - lastProbeRefreshRef.current;
                    if (sinceLast < PROBE_REFRESH_MIN_MS) {
                        return; // banner already cleared above; refresh will ride the next tick
                    }
                    lastProbeRefreshRef.current = Date.now();
                    // Reconnect: make sure the supabase CLIENT has a live
                    // session, not just our React state. A failed refresh makes
                    // the client wipe its own stored token; the app keeps
                    // running on the backup copy, but the client can no longer
                    // refresh — so the next relaunch kicks the user to the
                    // login page (Jill's iPad, 2026-07-11). Hand the backup
                    // tokens back to the client so it truly resumes.
                    try {
                        const { data } = await supabase.auth.getSession();
                        if (data?.session) {
                            supabase.auth.refreshSession().catch(() => { /* retried by the client */ });
                        } else {
                            const backup = await readCachedSessionDurable();
                            if (backup?.access_token && backup?.refresh_token) {
                                const { error } = await supabase.auth.setSession({
                                    access_token: backup.access_token,
                                    refresh_token: backup.refresh_token,
                                });
                                if (!error) return; // truly resumed — SIGNED_IN/TOKEN_REFRESHED will land
                                if (!/network|fetch|timeout|abort/i.test(error.message || "")) {
                                    // The server REFUSED the saved session (revoked,
                                    // not a network blip) — these tokens are dead.
                                    // Do NOT kick the user out of whatever they're
                                    // working on: keep running on the device
                                    // identity and raise the sign-in-to-sync banner.
                                    console.warn("AuthContext: saved session rejected by server — asking for re-sign-in", error);
                                    try { localStorage.removeItem("seapa_session_backup"); } catch { /* ignore */ }
                                    durableRemove("seapa_session_backup");
                                    if (mountedRef.current) setNeedsReauth(true);
                                }
                            } else {
                                // Online, but no tokens anywhere to resume with —
                                // the app is running on the token-less identity.
                                if (mountedRef.current) setNeedsReauth(true);
                            }
                        }
                    } catch { /* auth still flaky — the next probe tick retries */ }
                }
            } catch { /* still dark — try again next tick */ }
        };
        probe();
        const iv = setInterval(probe, 25000);
        const onFocus = () => probe();
        window.addEventListener("focus", onFocus);
        window.addEventListener("online", onFocus);
        document.addEventListener("visibilitychange", onFocus);
        return () => {
            stopped = true;
            clearInterval(iv);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("online", onFocus);
            document.removeEventListener("visibilitychange", onFocus);
        };
    }, [offline]);

    useEffect(() => {
        mountedRef.current = true;
        console.log("AuthContext: mounting...");

        // Local-first boot (Jill 2026-07-12: "why wait at all — it connects
        // later anyway?"). Everything needed to render — session, roles, last
        // data — is already on the device, so open the app IMMEDIATELY from the
        // saved session and stay optimistically ONLINE (no banner). The network
        // confirms in the background: a good connection silently takes over
        // (INITIAL_SESSION/TOKEN_REFRESHED), a dead one quietly raises the
        // banner a few seconds later (the check below). This replaces the old
        // timer that made everyone wait before booting AND flashed "offline" at
        // healthy users on every cold start. markOffline:false is the point —
        // never declare offline on a hunch. (No saved session = fresh device;
        // bootFromDevice returns false and we wait for a real login / failsafe.)
        bootFromDevice(false, { markOffline: false });

        // Confirm connectivity in the background and raise the offline banner
        // ONLY if a real check fails — pilots know when they're offline, so no
        // need to nag instantly (Jill 2026-07-12). Success is silent; failure
        // flips offline=true, which arms the reconnect probe (its own effect)
        // to clear the banner the moment signal returns. Deadline is
        // AbortController-based so it can't itself hang on dead wifi (old iPads
        // lack AbortSignal.timeout).
        (async () => {
            // Two-strike offline. A SINGLE slow/aborted health check used to
            // declare the whole app offline — even on good wifi — which armed
            // the reconnect probe and started the refresh cascade (2026-07-18:
            // Jill saw "offline" on stable wifi). One blip is not offline;
            // require two consecutive failures (or the browser's own offline
            // signal) before raising the banner.
            const checkHealth = async () => {
                let signal;
                if (typeof AbortSignal !== "undefined" && AbortSignal.timeout) {
                    signal = AbortSignal.timeout(5000);
                } else if (typeof AbortController !== "undefined") {
                    const c = new AbortController();
                    setTimeout(() => c.abort(), 5000);
                    signal = c.signal;
                }
                try {
                    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/auth/v1/health`, {
                        method: "GET",
                        cache: "no-store",
                        headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
                        ...(signal ? { signal } : {}),
                    });
                    return res.ok;
                } catch {
                    return false;
                }
            };
            // The browser being certain it's offline is trustworthy on its own.
            if (typeof navigator !== "undefined" && navigator.onLine === false) {
                if (mountedRef.current) setOffline(true);
                return;
            }
            if (await checkHealth()) return; // first strike passed — online
            if (!mountedRef.current) return;
            // Second opinion after a brief pause before committing to "offline".
            await new Promise((r) => setTimeout(r, 1500));
            if (!mountedRef.current) return;
            if (await checkHealth()) return; // recovered — no banner
            if (mountedRef.current) setOffline(true);
        })();

        // Failsafe: still stuck (no saved session to fall back on) — show the
        // recovery buttons rather than an endless spinner.
        const failsafeTimer = setTimeout(() => {
            if (loadingRef.current && mountedRef.current) {
                console.warn("AuthContext: Failsafe triggered! Forcing loading=false");
                setLongLoad(true);
                setLoading(false); // Force unlock
            }
        }, 15000);

        // Browser-level connectivity signals (fire on airplane mode / wifi off;
        // the connected-but-dead-wifi case is covered by the fallback above).
        // Note: the ACTIVE reconnect probe lives in its own effect below —
        // these events alone are not trusted to clear the banner.
        const onOffline = () => { if (mountedRef.current) setOffline(true); };
        window.addEventListener("offline", onOffline);

        async function initAuth() {
            try {
                console.log("AuthContext: calling getSession()...");
                // Short timeout (2s) for getSession - if it hangs, just rely on onAuthStateChange
                const { data, error } = await withTimeout(supabase.auth.getSession(), 2000);

                if (error) throw error;

                const initialSession = data?.session;

                if (mountedRef.current && initialSession) {
                    setSession(initialSession);
                    setUser(initialSession.user);
                    // Don't await full roles here if onAuthStateChange is about to fire
                    // But initAuth is often faster.
                    // Let's try to load roles, but not block 'setLoading' state if onAuthStateChange picks it up.
                }
            } catch (err) {
                console.warn("AuthContext: getSession/init skipped or timed out (non-critical if onAuthStateChange fires)", err);
            } finally {
                // Do not force setLoading(false) here blindly, as onAuthStateChange might be working.
                // But if we have no session and no listener event yet, we should stop loading?
                // Actually, onAuthStateChange fires 'INITIAL_SESSION' almost immediately.
                // We'll let the event handler manage the main loading state unlock.
            }
        }

        initAuth();

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
            console.log("AuthContext: onAuthStateChange", event);
            if (!mountedRef.current) return;

            // A refresh that was already in flight when Sign Out was tapped
            // can land afterwards with a fresh session: drop it. Only a
            // refresh: a real new sign-in (SIGNED_IN) during the cleanup wins,
            // and the cleanup then leaves its token alone (Codex review #506).
            if (newSession && signingOutRef.current) {
                if (event === "TOKEN_REFRESHED") {
                    console.warn("AuthContext: token refresh during sign-out ignored");
                    clearStoredAuthToken();
                    return;
                }
                signingOutRef.current = false;
                signOutSupersededRef.current = true;
            }

            if (newSession) realLoginSeenRef.current = true;
            // Any real login (a password sign-in, a reset link, a restored
            // token) ends the signed-out state; device boots never pass here.
            if (newSession && explicitSignOutRef.current) {
                explicitSignOutRef.current = false;
                setSignedOutExplicitly(false);
                clearSignedOut();
            }

            // Server-confirmed auth = network is really back; clear offline mode.
            if (newSession && (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN')) {
                setOffline(false);
                setNeedsReauth(false); // the server just honored this login
                // A live server-confirmed session = the moment to send any
                // submissions queued while offline.
                drainSubmitQueue();
                // A fresh sign-in re-arms the offline protection: without this,
                // one tap of Sign Out disabled the keep-running-on-saved-session
                // guard for every later login on that device.
                explicitSignOutRef.current = false;
                setSignedOutExplicitly(false);
                clearSignedOut();
                // Backup mirror: Supabase clears its own stored token when a
                // refresh fails, which would leave offline boots with nothing
                // to fall back on. Keep our own copy of the last good session —
                // in localStorage AND the IndexedDB durable mirror (iOS dropped
                // the localStorage copy on Jill's iPad, 2026-07-11).
                try {
                    const raw = JSON.stringify(newSession);
                    try { localStorage.setItem("seapa_session_backup", raw); } catch { /* quota */ }
                    durableSet("seapa_session_backup", raw);
                } catch { /* backup layer never blocks auth */ }
            }

            // A null session arriving WITHOUT the user tapping Logout is
            // network starvation or a failed token refresh, not a logout.
            // Do NOT trust navigator.onLine here — ship/PPU wifi reports
            // online while having no internet behind it (Jill's iPad mini,
            // 2026-07-07: switched to PPU wifi, got kicked to login, which
            // can't sign in offline). Keep running on the saved session;
            // Supabase re-syncs (TOKEN_REFRESHED) when the network is back.
            if (!newSession && !explicitSignOutRef.current) {
                const booted = await bootFromDevice(false);
                if (booted) {
                    console.warn("AuthContext: null-session event without a Logout tap — kept running on device storage");
                    return;
                }
                // Couldn't rebuild from device storage — but if the screen
                // already has a working session, this null event is starvation
                // noise. A signed-in pilot mid-slip is NEVER downgraded by a
                // background event; only a real Sign Out tap logs out (Jill's
                // iPad 2026-07-11: tapping into the slip offline kicked her to
                // an unusable login page).
                if (sessionRef.current) {
                    console.warn("AuthContext: null-session event ignored — keeping the session already on screen");
                    setOffline(true);
                    return;
                }
            }

            const newUserId = newSession?.user?.id;
            const previousUserId = lastUserIdRef.current;

            // Optimization: If user ID hasn't changed, DO NOT re-fetch roles.
            // This prevents "Token Refresh" or navigation events from triggering a blocking load.
            if (newUserId && newUserId === previousUserId) {
                console.log("AuthContext: User ID matches (", newUserId, "), skipping role refresh.");
                setSession(newSession);
                setUser(newSession?.user);
                // Committee positions still refresh on this path: the local-first
                // boot already claimed this user id, so INITIAL_SESSION always
                // lands here — without this, a login from before the positions
                // feature (no cache yet) would NEVER load them.
                loadCommitteePositions(newUserId).then((list) => {
                    if (mountedRef.current) setCommitteePositions(list);
                });
                setLoading(false); // Ensure we are not stuck loading
                return;
            }

            // If we are here, it's a new user or logout.
            lastUserIdRef.current = newUserId || null;

            if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
                // Only toggle loading if we don't have cache.
                const CACHE_KEY = `roles_${newUserId}`;
                if (!localStorage.getItem(CACHE_KEY)) {
                    setLoading(true);
                }
            }

            setSession(newSession);
            setUser(newSession?.user ?? null);

            if (newSession?.user) {
                setRolesLoaded(false); // new user — roles unknown until cache/fetch answers
                const userRoles = await loadRoles(newSession.user.id, (cachedRoles) => {
                    if (mountedRef.current) {
                        setRoles(cachedRoles);
                        setRolesLoaded(true);
                        setLoading(false); // Unblock UI!
                    }
                });
                if (mountedRef.current) {
                    setRoles(userRoles);
                    setRolesLoaded(true);
                    setLoading(false);
                }
                // Token-less identity for offline boots: who this device
                // belongs to, so losing the login TOKENS can never lock a
                // pilot out of their own local work.
                persistOfflineIdentity({
                    userId: newSession.user.id,
                    email: newSession.user.email || "",
                    roles: userRoles,
                    savedAt: Date.now(),
                });
                // Board position is supplementary — load after unblocking the UI.
                // Seed from cache synchronously so position-gated pages don't
                // bounce on a deep link while the real lookup is in flight.
                try {
                    const cachedPos = localStorage.getItem(`boardpos_${newSession.user.id}`);
                    if (cachedPos != null) { setBoardPosition(JSON.parse(cachedPos)); setPositionLoaded(true); }
                    const cachedCommittee = localStorage.getItem(`committeepos_${newSession.user.id}`);
                    if (cachedCommittee != null) setCommitteePositions(JSON.parse(cachedCommittee));
                } catch { /* ignore */ }
                loadCommitteePositions(newSession.user.id).then((list) => {
                    if (mountedRef.current) setCommitteePositions(list);
                });
                loadBoardPosition(newSession.user.id, newSession.user.email).then((pos) => {
                    if (mountedRef.current) { setBoardPosition(pos); setPositionLoaded(true); }
                    persistOfflineIdentity({
                        userId: newSession.user.id,
                        email: newSession.user.email || "",
                        roles: userRoles,
                        boardPosition: pos ?? null,
                        savedAt: Date.now(),
                    });
                });
            } else {
                if (mountedRef.current) {
                    setRoles([]);
                    setRolesLoaded(true); // logged out — roles definitively none
                    setBoardPosition(null);
                    setCommitteePositions([]);
                    setLoading(false);
                }
            }
        });


        return () => {
            console.log("AuthContext: unmounting");
            mountedRef.current = false;
            clearTimeout(failsafeTimer);
            window.removeEventListener("offline", onOffline);
            subscription.unsubscribe();
        };
    }, []);

    const value = {
        session,
        user,
        roles,
        rolesLoaded,
        boardPosition,
        committeePositions,
        positionLoaded,
        loading,
        offline,
        needsReauth,
        signedOutExplicitly,
        // Login page's "Continue offline" door: open the app from device
        // storage on an explicit tap (the automatic paths should make this
        // rare — it's the visible escape hatch if they somehow don't fire).
        continueOffline: () => bootFromDevice(false, { explicit: true }),
        signOut: async () => {
            console.log("AuthContext: signOut called");
            // Everything that stops the login coming back happens before the
            // first await (Codex review of #506): the in-flight-refresh guard,
            // the device token, and the signed-out flag. A refresh landing
            // during the slower cleanup below is then dropped, not adopted.
            signingOutRef.current = true;
            signOutSupersededRef.current = false;
            explicitSignOutRef.current = true;
            // Clear the login on the device FIRST, then tell the server if it
            // can be reached. Offline or on dead ship wifi, signOut()'s server
            // call fails or hangs and supabase-js then skips clearing its own
            // stored login, so the next launch booted straight back in; and a
            // pilot who closed the app before a timeout kept the token too.
            const accessToken = clearStoredAuthToken();
            setSignedOutExplicitly(true);
            markSignedOut();
            try {
                localStorage.removeItem("lastRoute");
                localStorage.removeItem("seapa_session_backup"); // a real logout kills the offline fallback too
            } catch { /* ignore */ }
            durableRemove("seapa_session_backup");
            // The token-less offline identity is KEPT (Jill 2026-09-24): a
            // pilot who signs out offline must still be able to open their
            // saved transit briefs from the login page's Continue offline
            // button. markSignedOut() above keeps the app from booting back in
            // on its own.
            setNeedsReauth(false);
            // The screen changes NOW. On dead wifi the client's own reset
            // queues behind a hung token refresh for up to 8 s, and a pilot
            // who sees nothing happen taps again (measured 2026-09-24).
            if (mountedRef.current) {
                setSession(null);
                setUser(null);
                setRoles([]);
                setBoardPosition(null);
                setCommitteePositions([]);
                setLoading(false);
            }
            // The service worker keeps a NetworkFirst copy of every Supabase
            // REST read (vite.config.js "supabase-rest"). It is keyed by URL,
            // not by who was signed in, so on a shared device the next login
            // could be served the previous pilot's rows while offline. Drop it.
            try {
                if (typeof caches !== "undefined") {
                    const names = await caches.keys();
                    await Promise.all(names.filter((n) => n.includes("supabase-rest")).map((n) => caches.delete(n)));
                }
            } catch { /* ignore — cache API unavailable in some contexts */ }
            // With storage already empty, the local signOut makes no network
            // call; it only resets the client.
            // A new sign-in during the cache cleanup above wins: don't reset
            // the client under it (Codex review of #506).
            if (signOutSupersededRef.current) { signingOutRef.current = false; revokeLoginOnServer(accessToken); return; }
            withTimeout(supabase.auth.signOut({ scope: "local" }), 8000)
                .catch((e) => console.warn("AuthContext: local sign-out did not finish", e))
                .finally(() => {
                    signingOutRef.current = false;
                    if (!signOutSupersededRef.current) clearStoredAuthToken();
                });
            revokeLoginOnServer(accessToken);
        }
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading ? children : (
                <div style={{
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: "1rem",
                    background: "#f8f9fa"
                }}>
                    <div className="spinner" style={{
                        width: 32,
                        height: 32,
                        border: "4px solid #ddd",
                        borderTopColor: "#003366",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite"
                    }}></div>
                    <style>{`@keyframes spin { 0 % { transform: rotate(0deg); } 100 % { transform: rotate(360deg); } } `}</style>
                    <span style={{ color: "#555", fontFamily: "sans-serif" }}>Loading SEAPA Portal...</span>

                    {/* Panic Button if stuck */}
                    {longLoad && (
                        <div style={{ marginTop: 20, textAlign: "center" }}>
                            <p style={{ color: "red", fontSize: "0.9rem" }}>Taking longer than expected...</p>
                            <button
                                onClick={() => {
                                    explicitSignOutRef.current = true;
                                    supabase.auth.signOut();
                                    window.location.href = '/login';
                                }}
                                style={{
                                    padding: "8px 16px",
                                    background: "#d9534f",
                                    color: "white",
                                    border: "none",
                                    borderRadius: 4,
                                    cursor: "pointer"
                                }}
                            >
                                Force Reset / Logout
                            </button>
                            <button
                                onClick={() => setLoading(false)}
                                style={{
                                    marginTop: 10,
                                    display: "block",
                                    background: "transparent",
                                    border: "none",
                                    color: "#003366",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    width: "100%"
                                }}
                            >
                                Try to render anyway
                            </button>
                        </div>
                    )}
                </div>
            )}
        </AuthContext.Provider>
    );
}
